import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import LoginNew from '@/components/LoginNew';
import { useQuery } from 'hooks/query';
import { ChainlitContext, useAuth } from '@chainlit/react-client';
import CbreLogoWhite from '@/assets/CBRE_white.svg'; // NEW: local SVG (bundled by Vite)

export const LoginError = new Error(
  'Error logging in. Please try again later.'
);

export default function Login() {
  const query = useQuery();
  const { data: config, user, setUserFromAPI } = useAuth();
  const [error, setError] = useState('');
  const apiClient = useContext(ChainlitContext);
  const navigate = useNavigate();

  const handleCookieAuth = (json: any): void => {
    if (json?.success != true) throw LoginError;
    setUserFromAPI();
  };

  const handleAuth = async (jsonPromise: Promise<any>, redirectURL?: string) => {
    try {
      const json = await jsonPromise;
      handleCookieAuth(json);
      if (redirectURL) navigate(redirectURL);
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleHeaderAuth = async () => {
    const jsonPromise = apiClient.headerAuth();
    handleAuth(jsonPromise, '/');
  };

  useEffect(() => {
    setError(query.get('error') || '');
  }, [query]);

  useEffect(() => {
    if (!config) return;
    if (user) {
      navigate('/');
      return;
    }
    if (config?.headerAuth) handleHeaderAuth();
  }, [config, user]);

  const redirect = query.get('redirect') || '/';
  const oauthProviders: string[] =
    (config as any)?.oauthProviders?.length
      ? (config as any).oauthProviders
      : ['oidc'];

  return (
    <div className="grid min-h-screen lg:grid-cols-1 bg-white" >
      <div
        className="relative min-h-screen flex items-center justify-center"
        style={{ backgroundColor: '#003F2E', 
          backgroundImage:`url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='1368' height='400' fill='none'%3e%3cmask id='a' width='1368' height='401' x='0' y='0' maskUnits='userSpaceOnUse'%3e%3cpath fill='url(%23paint0_linear)' d='M437 116C223 116 112 0 112 0h1256v400c-82 0-225-21-282-109-112-175-436-175-649-175z'/%3e%3cpath fill='url(%23paint1_linear)' d='M1368 400V282C891-29 788 40 711 161 608 324 121 372 0 361v39h1368z'/%3e%3cpath fill='url(%23paint2_linear)' d='M1368 244v156H0V94c92-24 198-46 375 0l135 41c176 51 195 109 858 109z'/%3e%3cpath fill='url(%23paint3_linear)' d='M1252 400h116c-14-7-35-14-116-16-663-14-837-128-1013-258l-85-61C98 28 46 8 0 0v400h1252z'/%3e%3c/mask%3e%3cg mask='url(%23a)'%3e%3cpath fill='white' d='M-172-98h1671v601H-172z'/%3e%3c/g%3e%3cdefs%3e%3clinearGradient id='paint0_linear' x1='602' x2='1093.5' y1='-960.5' y2='272' gradientUnits='userSpaceOnUse'%3e%3cstop stop-color='white'/%3e%3cstop offset='1' stop-color='white' stop-opacity='0'/%3e%3c/linearGradient%3e%3clinearGradient id='paint1_linear' x1='482' x2='480' y1='1058.5' y2='70.5' gradientUnits='userSpaceOnUse'%3e%3cstop stop-color='white'/%3e%3cstop offset='1' stop-color='white' stop-opacity='0'/%3e%3c/linearGradient%3e%3clinearGradient id='paint2_linear' x1='424' x2='446.1' y1='-587.5' y2='274.6' gradientUnits='userSpaceOnUse'%3e%3cstop stop-color='white'/%3e%3cstop offset='1' stop-color='white' stop-opacity='0'/%3e%3c/linearGradient%3e%3clinearGradient id='paint3_linear' x1='587' x2='349' y1='-1120.5' y2='341' gradientUnits='userSpaceOnUse'%3e%3cstop stop-color='white'/%3e%3cstop offset='1' stop-color='white' stop-opacity='0'/%3e%3c/linearGradient%3e%3c/defs%3e%3c/svg%3e"), linear-gradient(90deg, rgb(1, 42, 45), rgb(1, 42, 45))`,
        backgroundSize: 'cover' }}
      >
        {/* Logo top-left */}
        {/* <div className="absolute top-4 left-4">
          <img
            src={CbreLogoWhite}
            alt="CBRE"
            className="h-10 w-auto md:h-8 select-none"
            draggable={false}
          />
        </div> */}

        {/* Centered stack */}
        <div className="flex flex-1 items-center justify-center px-4">
            <div className="w-full max-w-md space-y-10">
            {/* Title block */}
            <div className="text-center">
            <div className="flex justify-center mb-4">
              <img
              src={CbreLogoWhite}
              alt="CBRE"
              className="h-10 w-auto md:h-8 select-none"
              draggable={false}
              />
            </div>
              <h1 className="text-white text-3xl md:text-4xl font-semibold leading-tight tracking-wide">
                Managed Services
              </h1>
              <p className="mt-2 text-sm md:text-base font-medium leading-tight tracking-wide">
                AIOps Portal
              </p>
              
            </div>

            {/* Card */}
            <div className="rounded-2xl bg-white/10 backdrop-blur-sm border border-white/15 shadow-[0_8px_30px_-6px_rgba(0,0,0,0.55)] p-6">
              {error && (
                <div className="mb-5 rounded-md border border-red-500/40 bg-red-500/15 text-red-200 px-3 py-2 text-xs flex gap-2">
                  <span>⚠️</span>
                  <span>{error}</span>
                </div>
              )}
              <LoginNew
                error={error}
                providers={oauthProviders}
                callbackUrl={redirect}
              />
            </div>

            {/* Footer */}
            <div className="text-center text-[11px] tracking-wide text-white/50">
              © {new Date().getFullYear()} CBRE Managed Services
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
